import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function ETFsAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Ticker Details - ETFs API" description="Query ticker details data via the ETFs API; request parameters, code examples and the response schema for the ticker details endpoint."  canonicalUrl="https://axionquant.com/docs/01-market-data/ETFs/06-ticker-details" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Ticker Details - ETFs API</DocHeading>
      <div className="space-y-12" ref={sectionRef}>
        {/* Get ETF Ticker Details */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/:ticker
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves detailed metadata for a specific ETF ticker. This endpoint provides essential information for a single ETF including its unique identifier, full name, and exchange listing.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        ETF ticker symbol (e.g., &quot;SPY&quot;, &quot;QQQ&quot;, &quot;IVV&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/etfs/SPY', '/etfs/QQQ']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">id <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Unique identifier for the ETF</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">name <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Full name of the ETF</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ticker <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">ETF ticker symbol</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">symbol <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Full symbol with exchange prefix</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">exchange <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Exchange where ETF is listed</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">market <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Market region (e.g., &quot;america&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">country <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Country of listing</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">type <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Fund type (e.g., etf fund)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">sector <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">ETF sector classification</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">industry <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">ETF industry classification</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">currency <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Trading currency (e.g., USD)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">lastClose <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Most recent closing price</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">changePct <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Percentage change since last close</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">issuer <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Issuer/management company of the ETF</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">description <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Detailed description of the ETF's investment objective and strategy</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">inception <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Inception date of the ETF (ISO 8601)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="ETF Ticker Details" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">ETF Ticker Details</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs/ticker"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/ticker-details"></Code>
              </div>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
}
